import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Briefcase, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';

const CareersPage = () => {
  const { language } = useLanguage();
  const t = translations.careers;
  
  // Track if mobile view
  const [isMobile, setIsMobile] = useState(false);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const carouselRef = useRef(null);
  
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Language-specific mobile checks
  const isZhMobile = language === 'zh' && isMobile;
  const isEnMobile = language === 'en' && isMobile;

  const testimonials = [
    t.testimonial1[language],
    t.testimonial2[language],
    t.testimonial3[language]
  ];

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  // Job Card Component - Different layouts for Chinese and English mobile
  const JobCard = ({ title, isJunior }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: isJunior ? 0 : 0.1 }}
      className="bg-white rounded-xl md:rounded-2xl shadow-sm hover:shadow-md transition-all border border-gray-100 overflow-hidden h-full flex flex-col"
      data-testid={`job-position-${isJunior ? 0 : 1}`}
    >
      {/* Card Header - Tighter on English mobile */}
      <div className={`bg-gradient-to-r from-primary/5 to-secondary/5 border-b border-gray-100 ${
        isEnMobile ? 'px-4 py-2.5' : 'px-4 py-3 md:px-6 md:py-5 lg:px-8 lg:py-6'
      }`}>
        <div className={`flex items-center ${isEnMobile ? 'gap-2.5' : 'gap-3 md:gap-4'}`}>
          <div className={`bg-secondary/10 flex-shrink-0 ${
            isEnMobile ? 'p-1.5 rounded-lg' : 'p-2 md:p-3 rounded-lg md:rounded-xl'
          }`}>
            <Briefcase className="text-secondary" size={isEnMobile ? 16 : (isMobile ? 18 : 22)} />
          </div>
          <h3 className={`font-semibold font-noto-serif text-primary leading-tight ${
            isEnMobile ? 'text-[15px]' : 'text-base md:text-lg lg:text-2xl'
          }`}>
            {title}
          </h3>
        </div>
      </div>

      {/* Card Body */}
      <div className={`flex-1 ${
        isEnMobile ? 'px-4 py-3' : 'px-6 py-6 lg:px-8 lg:py-8'
      }`}>
        <div>
          
          {/* Education & Experience - Stacked vertically on English mobile */}
          {isEnMobile ? (
            // English mobile: Stack vertically
            <div className="space-y-2.5">
              <div>
                <h4 className="text-[10px] font-bold text-secondary uppercase tracking-wider mb-0.5">
                  Education
                </h4>
                <p className="text-text-primary font-noto-sans text-sm leading-snug">
                  {t.educationReq[language]}
                </p>
              </div>
              <div>
                <h4 className="text-[10px] font-bold text-secondary uppercase tracking-wider mb-0.5">
                  Experience
                </h4>
                <p className="text-text-primary font-noto-sans text-sm leading-snug font-medium">
                  {isJunior ? t.expNone[language] : t.expTwoYears[language]}
                </p>
              </div>
              <div>
                <h4 className="text-[10px] font-bold text-secondary uppercase tracking-wider mb-0.5">
                  Language Requirement
                </h4>
                <p className="text-text-primary font-noto-sans text-sm leading-snug">
                  Chinese (Mandarin) required
                </p>
              </div>
            </div>
          ) : isZhMobile ? (
            // Chinese mobile: Side by side (original)
            <div className="grid grid-cols-2 gap-3">
              <div>
                <h4 className="text-[10px] font-bold text-secondary uppercase tracking-wider mb-1">
                  {t.education[language]}
                </h4>
                <p className="text-text-primary font-noto-sans text-sm leading-relaxed">
                  會計相關科系
                </p>
              </div>
              <div>
                <h4 className="text-[10px] font-bold text-secondary uppercase tracking-wider mb-1">
                  {t.experience[language]}
                </h4>
                <p className="text-text-primary font-noto-sans text-sm leading-relaxed font-medium">
                  {isJunior ? t.expNone[language] : t.expTwoYears[language]}
                </p>
              </div>
            </div>
          ) : (
            // Desktop: Use explicit spacing
            null
          )}

          {/* Desktop Education */}
          {!isMobile && (
            <div>
              <h4 className="text-xs font-bold text-secondary uppercase tracking-wider mb-2">
                {language === 'zh' ? t.education[language] : 'Education'}
              </h4>
              <p className="text-text-primary font-noto-sans text-[15px] leading-relaxed">
                {language === 'zh' ? '會計相關科系' : t.educationReq[language]}
              </p>
            </div>
          )}
          
          {/* Desktop Experience */}
          {!isMobile && (
            <div className="mt-5">
              <h4 className="text-xs font-bold text-secondary uppercase tracking-wider mb-2">
                {language === 'zh' ? t.experience[language] : 'Experience'}
              </h4>
              <p className="text-text-primary font-noto-sans text-[15px] leading-relaxed font-medium">
                {isJunior ? t.expNone[language] : t.expTwoYears[language]}
              </p>
            </div>
          )}
          
          {/* Desktop Language Requirement - English desktop only */}
          {!isMobile && language === 'en' && (
            <div className="mt-5">
              <h4 className="text-xs font-bold text-secondary uppercase tracking-wider mb-2">
                Language Requirement
              </h4>
              <p className="text-text-primary font-noto-sans text-[15px] leading-relaxed">
                Chinese (Mandarin) required
              </p>
            </div>
          )}

          {/* Key Responsibilities Section */}
          <div className={isEnMobile ? 'mt-3' : 'mt-6'}>
            <h4 className={`font-bold text-secondary uppercase tracking-wider ${
              isEnMobile ? 'text-[10px] mb-1.5' : 'text-xs mb-3'
            }`}>
              {language === 'zh' ? t.responsibilities[language] : 'Key Responsibilities'}
            </h4>
            <ul className={isEnMobile ? 'space-y-1' : 'space-y-2.5'}>
              {language === 'zh' ? (
                <>
                  <li className="flex items-baseline gap-2 md:gap-3">
                    <span className="w-1 h-1 md:w-1.5 md:h-1.5 bg-secondary rounded-full flex-shrink-0 mt-1.5"></span>
                    <span className="text-text-primary font-noto-sans text-sm md:text-[15px] leading-relaxed">{t.resp1[language]}</span>
                  </li>
                  <li className="flex items-baseline gap-2 md:gap-3">
                    <span className="w-1 h-1 md:w-1.5 md:h-1.5 bg-secondary rounded-full flex-shrink-0 mt-1.5"></span>
                    <span className="text-text-primary font-noto-sans text-sm md:text-[15px] leading-relaxed">{t.resp2[language]}</span>
                  </li>
                  <li className="flex items-baseline gap-2 md:gap-3">
                    <span className="w-1 h-1 md:w-1.5 md:h-1.5 bg-secondary rounded-full flex-shrink-0 mt-1.5"></span>
                    <span className="text-text-primary font-noto-sans text-sm md:text-[15px] leading-relaxed">{t.resp3[language]}</span>
                  </li>
                  <li className="flex items-baseline gap-2 md:gap-3">
                    <span className="w-1 h-1 md:w-1.5 md:h-1.5 bg-secondary rounded-full flex-shrink-0 mt-1.5"></span>
                    <span className="text-text-primary font-noto-sans text-sm md:text-[15px] leading-relaxed">{t.resp4[language]}</span>
                  </li>
                  {!isJunior && (
                    <li className="flex items-baseline gap-2 md:gap-3">
                      <span className="w-1 h-1 md:w-1.5 md:h-1.5 bg-secondary rounded-full flex-shrink-0 mt-1.5"></span>
                      <span className="text-text-primary font-noto-sans text-sm md:text-[15px] leading-relaxed">{t.resp5[language]}</span>
                    </li>
                  )}
                </>
              ) : isJunior ? (
                <>
                  <li className="flex items-baseline gap-2 md:gap-3">
                    <span className={`bg-secondary rounded-full flex-shrink-0 ${isEnMobile ? 'w-1 h-1 mt-1' : 'w-1 h-1 md:w-1.5 md:h-1.5 mt-1.5'}`}></span>
                    <span className={`text-text-primary font-noto-sans ${isEnMobile ? 'text-[13px] leading-snug' : 'text-sm md:text-[15px] leading-relaxed'}`}>Review accounting documents and vouchers</span>
                  </li>
                  <li className="flex items-baseline gap-2 md:gap-3">
                    <span className={`bg-secondary rounded-full flex-shrink-0 ${isEnMobile ? 'w-1 h-1 mt-1' : 'w-1 h-1 md:w-1.5 md:h-1.5 mt-1.5'}`}></span>
                    <span className={`text-text-primary font-noto-sans ${isEnMobile ? 'text-[13px] leading-snug' : 'text-sm md:text-[15px] leading-relaxed'}`}>Organize source documents and prepare entries</span>
                  </li>
                  <li className="flex items-baseline gap-2 md:gap-3">
                    <span className={`bg-secondary rounded-full flex-shrink-0 ${isEnMobile ? 'w-1 h-1 mt-1' : 'w-1 h-1 md:w-1.5 md:h-1.5 mt-1.5'}`}></span>
                    <span className={`text-text-primary font-noto-sans ${isEnMobile ? 'text-[13px] leading-snug' : 'text-sm md:text-[15px] leading-relaxed'}`}>Prepare financial analysis and schedules</span>
                  </li>
                  <li className="flex items-baseline gap-2 md:gap-3">
                    <span className={`bg-secondary rounded-full flex-shrink-0 ${isEnMobile ? 'w-1 h-1 mt-1' : 'w-1 h-1 md:w-1.5 md:h-1.5 mt-1.5'}`}></span>
                    <span className={`text-text-primary font-noto-sans ${isEnMobile ? 'text-[13px] leading-snug' : 'text-sm md:text-[15px] leading-relaxed'}`}>Assist with tax compliance filings</span>
                  </li>
                </>
              ) : (
                <>
                  <li className="flex items-baseline gap-2 md:gap-3">
                    <span className={`bg-secondary rounded-full flex-shrink-0 ${isEnMobile ? 'w-1 h-1 mt-1' : 'w-1 h-1 md:w-1.5 md:h-1.5 mt-1.5'}`}></span>
                    <span className={`text-text-primary font-noto-sans ${isEnMobile ? 'text-[13px] leading-snug' : 'text-sm md:text-[15px] leading-relaxed'}`}>Review accounting documents and vouchers</span>
                  </li>
                  <li className="flex items-baseline gap-2 md:gap-3">
                    <span className={`bg-secondary rounded-full flex-shrink-0 ${isEnMobile ? 'w-1 h-1 mt-1' : 'w-1 h-1 md:w-1.5 md:h-1.5 mt-1.5'}`}></span>
                    <span className={`text-text-primary font-noto-sans ${isEnMobile ? 'text-[13px] leading-snug' : 'text-sm md:text-[15px] leading-relaxed'}`}>Organize source documents and prepare entries</span>
                  </li>
                  <li className="flex items-baseline gap-2 md:gap-3">
                    <span className={`bg-secondary rounded-full flex-shrink-0 ${isEnMobile ? 'w-1 h-1 mt-1' : 'w-1 h-1 md:w-1.5 md:h-1.5 mt-1.5'}`}></span>
                    <span className={`text-text-primary font-noto-sans ${isEnMobile ? 'text-[13px] leading-snug' : 'text-sm md:text-[15px] leading-relaxed'}`}>Prepare financial analysis and schedules</span>
                  </li>
                  <li className="flex items-baseline gap-2 md:gap-3">
                    <span className={`bg-secondary rounded-full flex-shrink-0 ${isEnMobile ? 'w-1 h-1 mt-1' : 'w-1 h-1 md:w-1.5 md:h-1.5 mt-1.5'}`}></span>
                    <span className={`text-text-primary font-noto-sans ${isEnMobile ? 'text-[13px] leading-snug' : 'text-sm md:text-[15px] leading-relaxed'}`}>Handle tax compliance filings</span>
                  </li>
                  <li className="flex items-baseline gap-2 md:gap-3">
                    <span className={`bg-secondary rounded-full flex-shrink-0 ${isEnMobile ? 'w-1 h-1 mt-1' : 'w-1 h-1 md:w-1.5 md:h-1.5 mt-1.5'}`}></span>
                    <span className={`text-text-primary font-noto-sans ${isEnMobile ? 'text-[13px] leading-snug' : 'text-sm md:text-[15px] leading-relaxed'}`}>Proficient with Wen-Zhong software</span>
                  </li>
                </>
              )}
            </ul>
          </div>

          {/* Preferred Section - Both Chinese and English */}
          <div className={`border-t border-gray-100 ${
            isEnMobile ? 'mt-3 pt-2' : isZhMobile ? 'mt-4 pt-3' : 'mt-6 pt-5'
          }`}>
            <h4 className={`font-bold text-secondary uppercase tracking-wider ${
              isEnMobile ? 'text-[10px] mb-0.5' : isZhMobile ? 'text-[10px] mb-1' : 'text-xs mb-2'
            }`}>
              {language === 'zh' ? t.preferred[language] : 'Preferred'}
            </h4>
            <p className={`text-text-secondary font-noto-sans ${
              isEnMobile ? 'text-[13px] leading-snug' : isZhMobile ? 'text-sm leading-relaxed' : 'text-[15px] leading-relaxed'
            }`}>
              {t.preferredText[language]}
            </p>
          </div>

        </div>
      </div>
    </motion.div>
  );

  return (
    <div className="pt-16 md:pt-20" data-testid="careers-page">
      {/* Hero Section - Tighter on English mobile */}
      <section className={`bg-gradient-to-b from-primary/5 to-white ${
        isEnMobile ? 'py-5' : 'py-8 md:py-16 lg:py-24'
      }`}>
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className={`font-bold font-noto-serif text-primary ${
              isEnMobile ? 'text-2xl mb-2' : 'text-3xl md:text-4xl lg:text-5xl mb-3 md:mb-6'
            }`}>
              {t.pageTitle[language]}
            </h1>
            <p className={`text-text-secondary leading-relaxed font-noto-sans max-w-2xl mx-auto ${
              isEnMobile ? 'text-sm' : 'text-base md:text-lg lg:text-xl'
            }`}>
              {isMobile 
                ? (t.pageSubtitleMobile?.[language] || t.pageSubtitle[language])
                : t.pageSubtitle[language]
              }
            </p>
          </motion.div>
        </div>
      </section>

      {/* Recruitment Philosophy - Chinese Only */}
      {language === 'zh' && (
        <section className={isMobile ? 'py-4' : 'py-6 md:py-12 lg:py-16'}>
          <div className="container mx-auto px-4 md:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <div className={`grid grid-cols-1 lg:grid-cols-2 items-center ${
                isMobile ? 'gap-4' : 'gap-5 md:gap-8 lg:gap-12'
              }`}>
                {/* Image - Smaller on mobile */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5 }}
                  className="order-1"
                >
                  <img
                    src="https://images.pexels.com/photos/7651557/pexels-photo-7651557.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                    alt="招募理念 - 團隊合作"
                    className={`rounded-lg md:rounded-xl shadow-sm md:shadow-md w-full object-cover object-center ${
                      isMobile ? 'h-32' : 'h-40 md:h-64 lg:h-80'
                    }`}
                  />
                </motion.div>
                
                {/* Text Content */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                  className="order-2"
                >
                  <h2 className={`font-semibold font-noto-serif text-primary ${
                    isMobile ? 'text-xl mb-2' : 'text-2xl md:text-3xl lg:text-4xl mb-3 md:mb-5'
                  }`}>
                    {t.recruitPhilosophy[language]}
                  </h2>
                  
                  <p className={`text-text-primary font-noto-sans leading-relaxed ${
                    isMobile ? 'text-sm mb-3' : 'text-sm md:text-base mb-4 md:mb-6'
                  }`}>
                    {t.recruitPhilosophyDesc[language]}
                  </p>
                  
                  <ul className={isMobile ? 'space-y-2' : 'space-y-2.5 md:space-y-4'}>
                    <li className="flex items-baseline gap-2.5 md:gap-3">
                      <span className="w-1.5 h-1.5 md:w-2 md:h-2 bg-secondary rounded-full flex-shrink-0"></span>
                      <span className={`text-text-primary font-noto-sans leading-relaxed ${
                        isMobile ? 'text-xs' : 'text-sm md:text-[15px]'
                      }`}>
                        {t.recruitBullet1[language]}
                      </span>
                    </li>
                    <li className="flex items-baseline gap-2.5 md:gap-3">
                      <span className="w-1.5 h-1.5 md:w-2 md:h-2 bg-secondary rounded-full flex-shrink-0"></span>
                      <span className={`text-text-primary font-noto-sans leading-relaxed ${
                        isMobile ? 'text-xs' : 'text-sm md:text-[15px]'
                      }`}>
                        {t.recruitBullet2[language]}
                      </span>
                    </li>
                    <li className="flex items-baseline gap-2.5 md:gap-3">
                      <span className="w-1.5 h-1.5 md:w-2 md:h-2 bg-secondary rounded-full flex-shrink-0"></span>
                      <span className={`text-text-primary font-noto-sans leading-relaxed ${
                        isMobile ? 'text-xs' : 'text-sm md:text-[15px]'
                      }`}>
                        {t.recruitBullet3[language]}
                      </span>
                    </li>
                  </ul>
                </motion.div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Testimonials - Chinese Only - Carousel on mobile */}
      {language === 'zh' && (
        <section className={`bg-background-paper ${isMobile ? 'py-5' : 'py-8 md:py-16 lg:py-24'}`}>
          <div className="container mx-auto px-4 md:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className={`text-center ${isMobile ? 'mb-4' : 'mb-6 md:mb-12'}`}
            >
              <h2 className={`font-semibold font-noto-serif text-primary ${
                isMobile ? 'text-xl mb-1' : 'text-2xl md:text-3xl lg:text-4xl mb-2 md:mb-4'
              }`}>
                {t.testimonials[language]}
              </h2>
              <p className="text-sm md:text-base text-text-secondary font-noto-sans">{t.testimonialsSubtitle[language]}</p>
            </motion.div>

            {/* Mobile Carousel */}
            {isMobile ? (
              <div className="relative max-w-sm mx-auto">
                <div 
                  ref={carouselRef}
                  className="overflow-hidden"
                >
                  <motion.div
                    key={currentTestimonial}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3 }}
                    className="bg-white p-5 rounded-xl shadow-sm border-l-4 border-secondary/30"
                  >
                    <svg className="w-7 h-7 text-secondary/30 mb-3" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z"/>
                    </svg>
                    <p className="text-text-primary font-noto-sans leading-relaxed text-sm">{testimonials[currentTestimonial]}</p>
                  </motion.div>
                </div>
                
                {/* Carousel Controls */}
                <div className="flex items-center justify-center gap-4 mt-4">
                  <button 
                    onClick={prevTestimonial}
                    className="p-2 rounded-full bg-white shadow-sm border border-gray-200 text-primary hover:bg-gray-50 transition-colors"
                    aria-label="Previous"
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <div className="flex gap-1.5">
                    {testimonials.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentTestimonial(index)}
                        className={`w-2 h-2 rounded-full transition-colors ${
                          index === currentTestimonial ? 'bg-secondary' : 'bg-gray-300'
                        }`}
                        aria-label={`Go to testimonial ${index + 1}`}
                      />
                    ))}
                  </div>
                  <button 
                    onClick={nextTestimonial}
                    className="p-2 rounded-full bg-white shadow-sm border border-gray-200 text-primary hover:bg-gray-50 transition-colors"
                    aria-label="Next"
                  >
                    <ChevronRight size={18} />
                  </button>
                </div>
              </div>
            ) : (
              /* Desktop Grid */
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 max-w-6xl mx-auto">
                {testimonials.map((testimonial, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    className="bg-white p-6 lg:p-7 rounded-xl shadow-sm hover:shadow-md transition-shadow border-l-4 border-secondary/30"
                    data-testid={`testimonial-${index}`}
                  >
                    <svg className="w-8 h-8 text-secondary/40 mb-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z"/>
                    </svg>
                    <p className="text-text-primary font-noto-sans leading-relaxed text-base">{testimonial}</p>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Job Openings - Tighter spacing on English mobile */}
      <section className={`${language === 'zh' ? '' : 'bg-white'} ${
        isEnMobile ? 'py-4' : 'py-8 md:py-16 lg:py-24'
      }`}>
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className={`text-center ${isEnMobile ? 'mb-4' : 'mb-6 md:mb-12 lg:mb-16'}`}
          >
            <h2 className={`font-semibold font-noto-serif text-primary ${
              isEnMobile ? 'text-xl mb-1' : 'text-2xl md:text-3xl lg:text-4xl mb-2 md:mb-4'
            }`}>
              {t.jobOpenings[language]}
            </h2>
            <p className={`text-text-secondary font-noto-sans ${isEnMobile ? 'text-xs' : 'text-sm md:text-base'}`}>
              {t.jobOpeningsSubtitle[language]}
            </p>
          </motion.div>

          <div className={`grid grid-cols-1 xl:grid-cols-2 max-w-7xl mx-auto items-stretch ${
            isEnMobile ? 'gap-3' : 'gap-4 md:gap-8 lg:gap-10'
          }`}>
            <JobCard title={t.juniorTitle[language]} isJunior={true} />
            <JobCard title={t.seniorTitle[language]} isJunior={false} />
          </div>
        </div>
      </section>

      {/* Application Section - Updated English wording */}
      <section className={`bg-gradient-to-b from-primary/5 to-accent/5 ${
        isEnMobile ? 'py-5' : 'py-8 md:py-16 lg:py-20'
      }`}>
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-2xl mx-auto"
          >
            <h2 className={`font-semibold font-noto-serif text-primary ${
              isEnMobile ? 'text-xl mb-2' : 'text-2xl md:text-3xl lg:text-4xl mb-3 md:mb-6'
            }`}>
              {t.howToApply[language]}
            </h2>
            {language === 'en' ? (
              <p className={`text-text-primary font-noto-sans leading-relaxed ${isEnMobile ? 'text-sm' : 'text-sm md:text-base lg:text-lg'}`}>
                Please contact us by email or apply via the{' '}
                <a
                  href="https://www.104.com.tw/company/1a2x6bkvin"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-secondary hover:text-secondary/80 font-semibold underline underline-offset-2 inline-flex items-center gap-1"
                  data-testid="job-platform-link"
                >
                  job platform
                  <ExternalLink size={isEnMobile ? 12 : 14} />
                </a>.
              </p>
            ) : (
              <p className="text-sm md:text-base lg:text-lg text-text-primary font-noto-sans leading-relaxed">
                {t.applyText[language]}
                <a
                  href="https://www.104.com.tw/company/1a2x6bkvin"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-secondary hover:text-secondary/80 font-semibold underline underline-offset-2 inline-flex items-center gap-1"
                  data-testid="job-platform-link"
                >
                  {t.jobPlatform[language]}
                  <ExternalLink size={isMobile ? 14 : 16} />
                </a>
                {t.applyTextEnd[language]}
              </p>
            )}
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CareersPage;
